import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Dialog, DialogTitle, DialogContent, IconButton, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import axios from 'axios';
import { SERVER_NAME } from './config';
import { jsPDF } from 'jspdf'; // Import jsPDF
import { FaDownload } from 'react-icons/fa';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import html2canvas from 'html2canvas';


const useStyles = makeStyles((theme) => ({
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
        margin: 0,
        padding: 0,
        width: '100%',
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),  // Keep it aligned to the right
        top: theme.spacing(0.1),  // Reduce the top value to move it higher
        color: theme.palette.grey[500],
        zIndex: 1500,  // Ensure it's above other content
    },
    downloadButton: {
        color: theme.palette.primary.main,
        backgroundColor: '#ff6600',
        color: '#ffffff',
        padding: '3px 6px',
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: '#e65c00',
        },
        '& svg': { // Assuming the FontAwesome icon renders as an <svg> element
          fontSize: '0.9em',
        },
    },
    dialogPaper: {
        boxShadow: 'none',  // Keep shadows (adjust the level as needed)
        borderTop: 'none',            // Specifically remove the top border
        border: 'none',              // Remove the border
        width: '100%',
    },
    dialogTitle: {
        borderTop: 'none', // Ensure there's no border at the top
        borderBottom: 'none', // This removes the bottom border
        paddingBottom: 0,
        paddingTop: 0, // To minimize space
 
    },
    dialog: {
        width: '100%',
        maxWidth: '115vw', // Full width for mobile, custom width for desktop
        margin: '0',
        paddingTop: 0, // Reduce space at the top of the dialog
    },
    content: {
        marginTop: 0, // Remove or reduce margin to decrease space between title and content
        overflowX: 'hidden',            // Prevent horizontal scrolling
    },
    contentMobile: {
        marginTop: 0, // Remove or reduce margin to decrease space between title and content
        overflowX: 'hidden',            // Prevent horizontal scrolling
    },
    container: {
        width: '100%',
        overflowX: 'hidden', // Prevent horizontal overflow
    },
}));

export const Newsletter = ({ open, onClose, newsDate, setNewsDate }) => {
    const classes = useStyles();
    //const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const [newsletterHtml, setNewsletterHtml] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const firstTimeRef = useRef(true);

    // Function to update window width on resize
    const handleResize = () => {
        setWindowWidth(window.innerWidth - 50);
    };

    // Add and clean up event listener for window resize
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Determine if mobile based on width
    const isMobile = windowWidth <= 1000;

    // Function to preprocess HTML content before injecting
    function preprocessHtml(htmlContent) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        
        // Process links - replace target="_blank" with data-external-link="true"
        const links = tempDiv.querySelectorAll('a[target="_blank"]');
        console.log(`Processing ${links.length} external links in newsletter`);
        links.forEach(link => {
            link.removeAttribute('target');
            link.setAttribute('data-external-link', 'true');
        });
        
        return tempDiv.innerHTML;
    }
    
    // Function to handle injecting HTML and executing scripts
    function injectNewsletterContent(htmlContent) {
        // Preprocess HTML to handle external links
        const processedHtml = preprocessHtml(htmlContent);
        setNewsletterHtml(processedHtml); // Set the processed HTML content
    
        // After setting the HTML content, handle script execution
        setTimeout(() => {
            // Remove all previously added scripts
            const existingScripts = document.body.querySelectorAll('script[data-newsletter-script]');
            console.log('Existing scripts before removal:', existingScripts); // Log existing scripts
            existingScripts.forEach(script => {
                console.log('Removing script:', script); // Log each script being removed
                script.remove();
            });
    
            // Verify removal
            const remainingScripts = document.body.querySelectorAll('script[data-newsletter-script]');
            console.log('Remaining scripts after removal:', remainingScripts); // Log remaining scripts
    
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlContent;
    
            // Extract script tags
            const scripts = tempDiv.querySelectorAll('script');
            scripts.forEach(script => {
                const newScript = document.createElement('script');
                newScript.setAttribute('data-newsletter-script', 'true'); // Mark the script for future removal
                if (script.src) {
                    newScript.src = script.src;
                } else {
                    newScript.textContent = script.textContent;
                }
                document.body.appendChild(newScript);  // Append the script to the body to execute it
                console.log('Added new script:', newScript); // Log each new script being added
            });
        }, 100); // Adjust timeout if necessary
    }
    
    // Fetch the latest newsletter content
    useEffect(() => {
        const fetchNewsletter = async () => {
            try {
                setLoading(true);
                const baseURL = `${SERVER_NAME}/getNewsletter`;
                const url = newsDate ? `${baseURL}?newsDate=${newsDate}` : baseURL;
                //setNewsDate(null); // Reset the newsDate after fetching the newsletter
                const response = await axios.get(url);
                if (response.status === 200) {
                    injectNewsletterContent(response.data); // Inject the newsletter content
                } else {
                    setError('Failed to fetch the latest newsletter');
                }
            } catch (error) {
                setError('Error fetching the latest newsletter');
            } finally {
                setLoading(false);
            }
        };
    
        if (open && firstTimeRef.current) {
            firstTimeRef.current = false; // Update the ref to ensure it doesn't run again
            fetchNewsletter();
        }
    }, [open]);

    // Attach the "Download PDF" event listener after the content is loaded
    useEffect(() => {
        if (!loading && !error) {
            const downloadButton = document.getElementById('downloadPDF');
            if (downloadButton) {
                downloadButton.addEventListener('click', handleDownloadPDF);
            }

            return () => {
                if (downloadButton) {
                    downloadButton.removeEventListener('click', handleDownloadPDF);
                }
            };
        }
    }, [loading, error, newsletterHtml]);
    
    // Handle external links with target="_blank" to avoid React errors
    useEffect(() => {
        if (!loading && !error) {
            const newsletterContainer = document.getElementById('newsletter');
            if (newsletterContainer) {
                // Function to handle external link clicks
                const handleExternalLinkClick = (e) => {
                    const target = e.target.closest('a[data-external-link="true"]');
                    if (target) {
                        e.preventDefault();
                        window.open(target.href, '_blank', 'noopener,noreferrer');
                    }
                };
                
                // Add click listener
                newsletterContainer.addEventListener('click', handleExternalLinkClick);
                console.log('Added external link handler to newsletter container');
                
                // Cleanup
                return () => {
                    newsletterContainer.removeEventListener('click', handleExternalLinkClick);
                };
            }
        }
    }, [loading, error, newsletterHtml]);

    // Function to handle PDF download
    const handleDownloadPDF = () => {
        const container = document.querySelector('.newsletter-container');
    
        if (!container) {
            console.error("Error: The container element does not exist.");
            return;
        }
    
        html2canvas(container, { scale: 2 }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = 210; // A4 width in mm (210mm)
            const pageHeight = 297; // A4 height in mm (297mm)
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const pdf = new jsPDF('p', 'mm', 'a4');
    
            let y = 0; // Current position in PDF
    
            while (y < imgHeight) {
                pdf.addImage(imgData, 'PNG', 0, -y, imgWidth, imgHeight);
                y += pageHeight; // Move to next page
                if (y < imgHeight) pdf.addPage(); // Add new page if needed
            }
    
            pdf.save('E3D_Newsletter.pdf');
        }).catch((error) => {
            console.error("Error generating PDF:", error);
        });
    };
    
    return (
        <Dialog 
            open={open && !loading} 
            onClose={onClose} 
            aria-labelledby="newsletter-dialog-title" 
            className={classes.overlay}
            classes={{ paper: classes.dialog }} // Add responsive dialog class
            fullWidth={true} // Ensure it takes up full width of the container
            maxWidth="lg" // Use Material-UI's predefined size
            style={{ width: isMobile ? windowWidth : '100%', maxWidth: isMobile ? windowWidth : '100%' }} // Inline style for width
        >
            <DialogTitle id="newsletter-dialog-title" className={classes.dialogTitle}>
                <Tooltip title="Download as PDF">
                    <IconButton onClick={() => handleDownloadPDF()} className={classes.downloadButton}>
                        <FaDownload /> 
                    </IconButton>
                </Tooltip>
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={isMobile ? classes.contentMobile : classes.content}>
                {loading ? (
                    <p>Loading...</p>
                ) : error ? (
                    <p>{error}</p>
                ) : (
                    <div
                        id="newsletter"
                        className={classes.container}
                        dangerouslySetInnerHTML={{ __html: newsletterHtml }} // React injects the content
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

